const { BaseStorage } = require("@/storage/BaseStorage");

const storageKey = "of-netlogic-config";

const eventKey = `${storageKey}:changed`;

import * as Yup from "yup";

export class NetLogicTableConfiguration {
  /**
   *
   * @param {Object} options - Opções de configuração
   * @param {string} options.id - Id da mesa do Olga (opcional)
   * @param {string} options.externalId - Id da mesa da NetLogic
   * @param {string} options.apiUrl - URL da API da NetLogic
   * @param {string} options.apiToken - Token da API da NetLogic
   * @param {string} options.orderEmployee - ID do garçom/funcionário da mesa
   * @param {string} options.orderType - Tipo do pedido da NetLogic
   * @param {string} options.revenueCenter - Config revenueCenter da NetLogic
   * @param {string} options.tenderMediaClosing - Código da pré-fechamento da NetLogic
   * @param {string} options.tenderMediaIncrement - Código de incremento de itens da NetLogic
   * @param {string} options.callWaiterSku - Código do produto enviado ao chamar o garçom
   * @param {string} options.callWaiterClosingSku - Código do produto enviado ao chamar o garçom para fechamento (opcional)
   * @param {BaseStorage} options.storage - Instancia do serviço de armazenamento
   */

  constructor({
    id,
    externalId,
    apiUrl,
    apiToken,
    orderEmployee,
    orderType,
    revenueCenter,
    tenderMediaClosing,
    tenderMediaIncrement,
    callWaiterSku,
    callWaiterClosingSku,
    storage,
  }) {
    /**
     * Id da mesa do Olga.
     * @type {string}
     */
    this.id = id;

    /**
     * Id da mesa da NetLogic.
     * @type {string}
     */
    this.externalId = externalId;

    /**
     * URL da API da NetLogic.
     * @type {string}
     */
    this.apiUrl = apiUrl;

    /**
     * Token da API da NetLogic.
     * @type {string}
     */
    this.apiToken = apiToken || process.env.NEXT_PUBLIC_NETLOGIC_TOKEN;

    /**
     * Id do garçom/funcionário da mesa.
     * @type {string}
     */
    this.orderEmployee = orderEmployee;

    /**
     * Tipo do pedido da NetLogic.
     * @type {string}
     */
    this.orderType = orderType;

    /**
     * Config revenueCenter da NetLogic.
     * @type {string}
     */
    this.revenueCenter = revenueCenter;

    /**
     * Código da pré-fechamento da NetLogic.
     * @type {string}
     */
    this.tenderMediaClosing = tenderMediaClosing;

    /**
     * Código de incremento de itens da NetLogic.
     * @type {string}
     */
    this.tenderMediaIncrement = tenderMediaIncrement;

    /**
     * Código do produto enviado no pré-fechamento da mesa
     * @type {string}
     */
    this.callWaiterSku = callWaiterSku;

    /**
     * Código do produto enviado ao chamar o garçom para fechamento (opcional)
     * @type {string}
     */
    this.callWaiterClosingSku = callWaiterClosingSku;

    /**
     * Instancia do serviço de armazenamento.
     * @type {BaseStorage}
     */
    this.storage = storage;
  }

  static fromStorage() {
    const storage = new BaseStorage({ key: storageKey });

    const configs = storage.getData();

    const tableConfiguration = new NetLogicTableConfiguration({
      ...configs,
      storage,
    });

    tableConfiguration.updateStatus();

    return tableConfiguration;
  }

  update(configs) {
    this.externalId = configs?.externalId || this.externalId;
    this.apiUrl = configs?.apiUrl || this.apiUrl;
    this.apiToken = configs?.apiToken || this.apiToken;
    this.orderEmployee = configs?.orderEmployee || this.orderEmployee;
    this.orderType = configs?.orderType || this.orderType;
    this.revenueCenter = configs?.revenueCenter || this.revenueCenter;
    this.tenderMediaClosing =
      configs?.tenderMediaClosing || this.tenderMediaClosing;
    this.tenderMediaIncrement =
      configs?.tenderMediaIncrement || this.tenderMediaIncrement;
    this.callWaiterSku = configs?.callWaiterSku || this.callWaiterSku;
    this.callWaiterClosingSku = configs?.callWaiterClosingSku || this.callWaiterClosingSku;

    this.updateStatus();

    if (window) {
      this.storage.setData(this);

      window.dispatchEvent(
        new CustomEvent(eventKey, {
          detail: { storage: this.storage.id, key: this.storage.getKey() },
        })
      );
    }
  }

  addListener(handler) {
    if (!this.listener) {
      this.listener = (event) => {
        if (event.detail.key === this.storage.getKey()) {
          handler();
        }
      };
      window.addEventListener(eventKey, this.listener);
    }
  }

  removeListener() {
    if (this.listener) {
      window.removeEventListener(eventKey, this.listener);
      this.listener = null;
    }
  }

  export() {
    const { storage, listener, ...configs } = this;
    return {
      ...configs,
      callWaiterSku: this.callWaiterSku,
      callWaiterClosingSku: this.callWaiterClosingSku,
    };
  }

  import (rawConfig) {
    try {
      const configObject = JSON.parse(rawConfig);

      const schema = Yup.object({
        orderType: Yup.string()
          .required("Campo obrigatório"),
        apiUrl: Yup.string()
          .required("Campo obrigatório"),
        revenueCenter: Yup.string()
          .optional(),
        tenderMediaClosing: Yup.string()
          .optional(),
        tenderMediaIncrement: Yup.string()
          .optional(),
        callWaiterSku: Yup.string()
          .optional(),
        callWaiterClosingSku: Yup.string()
          .optional(),
      });

      const configs = schema.validateSync(configObject, { strict: true });

      this.update(configs);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const field = err.path;
        const error = err.errors[0];
        const errorMessage = [field, error].filter(Boolean).join(" - ");
        throw new Error(errorMessage);
      }

      throw new Error(err.message);
    }
  }

  isConfigured() {
    return !!(
      this.externalId &&
      this.apiUrl &&
      this.apiToken &&
      this.orderEmployee &&
      this.orderType &&
      this.tenderMediaIncrement &&
      this.tenderMediaClosing &&
      this.callWaiterSku
    );
  }

  updateStatus() {
    this.configured = this.isConfigured();
  }
}
